import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';

// @ts-ignore
import shopify from 'shopify';

import { ProductSellingGroups } from '@arctheme-components/ecommerce/Product/ProductSellingGroups/ProductSellingGroups';
import { ProductTags } from './ProductTags/ProductTags';
import { ProductRatings } from '@arctheme-components/ecommerce/Product/ProductRatings/ProductRatings';
import { ProductPrice } from './ProductPrice/ProductPrice';
import { ProductImages } from './ProductImages/ProductImages';
import { ProductSwatches } from '@arctheme-components/ecommerce/Product/ProductSwatches/ProductSwatches';
import QuickAdd from './ProductTileQuickAdd/ProductTileQuickAdd';
import useMobileBreakpoint from '@arctheme-hooks/useMobileBreakpoint';
import { mobileBreakpoint } from '../../../../entrypoints/theme';
import { ProductWishlist } from '@src/stories/ecommerce/Product/ProductWishlist/ProductWishlist';

export const ProductTile = ({
  product,
  swatches = [],
  siblings,
  show_add = false,
  show_price_inline = false,
  settings,
  enableWishlist,
  showPrice = true,
  showTags = true,
  showQuickAdd = true,
  type = ''
}) => {
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [hasRetrievedMetafields, sethasRetrievedMetafields] = useState(false);
  const [colourName, setColourName] = useState([]);
  const [isHovered, setIsHovered] = useState(false);

  const isMobile = useMobileBreakpoint(mobileBreakpoint);

  useEffect(() => {
    async function getMetafieldsAndSwatches() {
      const productMetafields = await shopify.getMetafieldsForProduct([`gid://shopify/Product/${product?.id}`]);

      product.metafields = productMetafields[0]?.metafields;

      let swatchProducts: any = [];

      for (const metafield of product.metafields) {
        switch (metafield.key) {
          case 'bottle_colour_name':
            setColourName(metafield.value[0]);
            break;
          // case "bottle_colour":
          //   currentColourHex = metafield.value[0];
          //   break;
          case 'bottle_siblings':
            const relatedProductIds = [...metafield.value];

            const relatedProducts = await shopify.getMetafieldsForProduct(relatedProductIds);
            const processedRelatedProducts = new Set();

            relatedProducts.forEach((relatedProduct) => {
              const swatchProduct = {
                url: `/products/${relatedProduct.handle}`, // Assuming `handle` is the URL or can be constructed to form the URL
                color: null,
                image: relatedProduct.image?.src || '', // Assuming `image` field exists and is directly accessible
                name: '' // Assuming `title` is the name
              };

              relatedProduct.metafields.forEach((relatedProductMetafield) => {
                if (relatedProductMetafield.key === 'bottle_colour_name') {
                  swatchProduct.name = relatedProductMetafield.value[0];
                }
                if (relatedProductMetafield.key === 'bottle_colour') {
                  swatchProduct.color = relatedProductMetafield.value[0];
                }
              });
              if (!processedRelatedProducts.has(swatchProduct.name)) {
                swatchProducts.push(swatchProduct);
                processedRelatedProducts.add(swatchProduct.name); // Mark this URL as processed
              }
            });
            break;
        }
      }

      sethasRetrievedMetafields(true);
      setRelatedProducts(swatchProducts);
    }

    if (isHovered && !hasRetrievedMetafields) {
      getMetafieldsAndSwatches();
    }
  }, [isHovered]);

  const createKeyValuePairs = (tags) => {
    const tagsArray = Array.isArray(tags) ? tags : [tags];

    return tagsArray.reduce((acc, tag) => {
      const [key, value] = tag.split(':');
      if (value) {
        if (acc[key]) {
          if (Array.isArray(acc[key])) {
            acc[key].push(value);
          } else {
            acc[key] = [acc[key], value];
          }
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});
  };

  const productSwatches: any = [];
  const variantFirstId = product?.variants[0]?.id || product?.id;
  const isAvailable = product?.variants[0]?.available;

  // console.log(`settings`, settings?.globals);
  // const showQuickAdd = settings?.globals?.product_tiles?.quickview_add_to_cart_enabled;

  showPrice = settings?.show_price ?? settings?.globals?.show_price;
  showTags = settings?.show_tags ?? true;
  showQuickAdd = settings?.show_quickadd ?? settings?.globals?.product_tiles?.quickview_add_to_cart_enabled;

  const showRating = false;

  let pricesClass = 'h5';
  let titleClass = 'h5';

  if (isMobile) {
    pricesClass = 'h6';
    titleClass = 'h6';
  }

  if (product?.tags) {
    const productTags = createKeyValuePairs(product?.tags);

    if (productTags?.colour) {
      const colors = Array.isArray(productTags.colour) ? productTags.colour : [productTags.colour];

      colors.forEach((colorHandle) => {
        const siblingProduct = siblings?.find((sibling) => sibling.handle === colorHandle);
        let isProduct = false;
        const matchingProduct = siblings?.find((sibling) => sibling.handle === product?.handle);

        if (matchingProduct === siblingProduct) {
          isProduct = true;
        }
        if (siblingProduct) {
          productSwatches.push({
            url: '/products/' + siblingProduct.handle,
            name: product.title,
            image:
              siblingProduct.images?.find((image) => image.src?.match(/_s_|_s(\.|$)/))?.src ||
              siblingProduct.images[siblingProduct.images?.length - 1].src,
            is_product: isProduct
          });
        }
      });
    }

    if (relatedProducts) {
      const colors = Array.isArray(productTags.colour) ? productTags.colour : [productTags.colour];

      colors.forEach((colorHandle) => {
        const relatedProduct = relatedProducts?.find((related) => related.handle === colorHandle);
        let isProduct = false;
        const matchingProduct = relatedProducts?.find((related) => related.handle === product.handle);

        if (matchingProduct === relatedProduct) {
          isProduct = true;
        }
        if (relatedProduct) {
          productSwatches.push({
            url: '/products/' + relatedProduct.handle,
            name: product.title,
            image:
              relatedProduct.images?.edges?.find((edge) => edge.node.src?.match(/_s_|_s(\.|$)/))?.node.src ||
              relatedProduct.images?.edges[relatedProduct.images.edges.length - 1]?.node.src,
            is_product: isProduct
          });
        }
      });
    }
  }

  if (product?.images?.length <= 0) {
    product.images.push({
      src: 'https://cdn.shopify.com/s/files/1/0592/8940/3562/files/placeholder.jpg?v=1726554438'
    });
  }
  let productUrl = `/products/`;
  if (settings?.collection){
    productUrl = `/collections/${settings.collection?.handle}/products/`;
    // productUrl = `/products/`;
  }

  return (
    <div
      className={`theme__product-card ${isHovered ? 'theme__product-card--hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className='productcard__content'>
        <div className='relative'>
          <ProductImages
            productUrl={`${productUrl}` + product?.handle}
            product={product}
          />
          {!isMobile && (
            <>
              {showQuickAdd && (
                <QuickAdd
                  product={product}
                  isHovered={isHovered}
                />
              )}

              {enableWishlist && (
                <div
                  className={`absolute top-0 right-0 transition-opacity duration-300 ${
                    isHovered ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <ProductWishlist
                    product={product}
                    type='plp'
                  />
                </div>
              )}
            </>
          )}
        </div>

        {showRating && (
          <div className='flex justify-between w-full mt-1 lg:mt-2 max-lg:items-center'>
            <ProductRatings product={product} />
          </div>
        )}

        <ajax-cart-product-form class='w-full'>
          <form
            action='/cart/add'
            id='product_form_id'
            className='product-form'
            noValidate
            data-prodify-product-form
          >
            <div className='flex justify-between mt-3 lg:mt-[13px] gap-x-2 gap-y-2 flex-col lg:flex-row'>
              <div className='flex flex-col'>
                <a href={'/products/' + product?.handle}>
                  <h2 className={`${titleClass}`}>{product?.title}</h2>
                </a>
                <div className={`mt-[7px] lg:mt-[5px] ${pricesClass}`}>
                  {showPrice && <ProductPrice product={product} />}
                </div>
              </div>
              {/* {showQuickAdd && <div className="flex ml-auto shrink-0 max-lg:w-full">
                <input type="hidden" name="id" value={`${variantFirstId}`} />
                {isAvailable
                  ? <button className={`button button-tertiary`}>Quick add</button>
                  : <button className={`button button-tertiary`} disabled>Out of stock</button>
                }
              </div>} */}
            </div>

            <div
              data-ajax-cart-errors='form'
              className='text-xs text-error-1 mt-2'
            ></div>
          </form>
        </ajax-cart-product-form>

        <div className='flex justify-between h-full'>
          {showTags && <ProductTags
            product={product}
            settings={settings}
            isMobile={isMobile}
          />}
          {enableWishlist && isMobile && (
            <ProductWishlist
              product={product}
              type='plp'
              isMobile={isMobile}
            />
          )}
        </div>

        {product?.selling_plan_groups?.length > 0 && (
          <div
            className={clsx('product__selling-groups overflow-hidden transition-all duration-300', {
              'max-h-0 opacity-0': !isHovered,
              'max-h-96 opacity-100 product-tile-hovered': isHovered
            })}
          >
            <div className='flex flex-col mt-4'>
              {relatedProducts.length > 0 && (
                <ProductSwatches
                  swatches={relatedProducts}
                  currentColor={colourName}
                  showName={true}
                />
              )}
              <ProductSellingGroups
                product={product}
                showAddToCart={true}
                isInTile
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductTile;
